import React, { useEffect, useState } from "react";
import { api, formatDate, formatDateTime, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DismissibleMemo } from "@/components/DismissibleMemo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, ShoppingCart, ArrowRight } from "lucide-react";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";
import { useNavigate } from "react-router-dom";

const STATUS_META = {
  pending: { label: "Menunggu", color: "bg-amber-100 text-amber-700 hover:bg-amber-100", icon: Clock },
  approved: { label: "Disetujui", color: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100", icon: CheckCircle2 },
  rejected: { label: "Ditolak", color: "bg-red-100 text-red-700 hover:bg-red-100", icon: XCircle },
  cancelled: { label: "Dibatalkan", color: "bg-slate-200 text-slate-700 hover:bg-slate-200", icon: XCircle },
};

const NOTE_TEMPLATES = [
  "Disetujui sesuai stok tersedia. Jadwal pengiriman akan dikonfirmasi terpisah.",
  "Sebagian jumlah disesuaikan sesuai stok gudang saat ini.",
  "Mohon konfirmasi ulang kebutuhan dan target pengiriman berikutnya.",
  "Belum dapat dipenuhi saat ini. Silakan ajukan kembali setelah koordinasi.",
];

function OrderItems({ order }) {
  const [open, setOpen] = useState(false);
  return <TooltipProvider delayDuration={150}><Tooltip open={open} onOpenChange={setOpen}>
    <TooltipTrigger asChild>
      <button type="button" onClick={() => setOpen(value => !value)} className="text-left border-b border-dotted border-slate-400 hover:text-emerald-700 focus-visible:outline-emerald-600" data-testid={`order-items-${order.id}`} aria-label="Lihat item dan catatan pesanan">
        {(order.items || []).map(item => `${item.product_name} × ${item.qty}`).join(", ")}
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-sm max-h-80 overflow-y-auto bg-white text-slate-800 border border-slate-200 shadow-lg p-3" data-testid={`order-notes-${order.id}`}>
      <div className="font-semibold mb-2">Catatan Pesanan</div>
      <div className="space-y-2 whitespace-pre-wrap break-words">
        {(order.items || []).map((item, index) => <div key={index}><b>{item.product_name} × {item.qty}</b><p>{item.notes || "Tidak ada catatan item"}</p></div>)}
        {order.notes && <div className="border-t pt-2"><b>Catatan umum:</b><p>{order.notes}</p></div>}
        {(order.approved_items || []).filter(item => item.notes).map((item, index) => <div key={`approved-${index}`} className="border-t pt-2"><b>Catatan persetujuan {item.product_name}:</b><p>{item.notes}</p></div>)}
      </div>
    </TooltipContent>
  </Tooltip></TooltipProvider>;
}

export default function AdminOrders() {
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState("approve"); // approve | reject
  const [note, setNote] = useState("");
  const [adjustedItems, setAdjustedItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const pendingTF = useTableFilter(orders.filter((o) => o.status === "pending"), {
    searchKeys: ["store_name", "mitra_name", "notes"], defaultSort: { key: "created_at", dir: "desc" },
  });
  const historyTF = useTableFilter(orders.filter((o) => o.status !== "pending"), {
    searchKeys: ["store_name", "mitra_name", "status", "owner_note"], defaultSort: { key: "decided_at", dir: "desc" },
  });

  const openReview = (o) => {
    setSelected(o);
    setAction("approve");
    setNote("");
    setAdjustedItems((o.items || []).map((item) => ({
      product_id: item.product_id,
      product_name: item.product_name,
      qty: item.qty,
      notes: "",
    })));
  };

  const updateAdjustedItem = (index, key, value) => {
    setAdjustedItems((items) => items.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [key]: value } : item
    )));
  };

  const submit = async () => {
    if (!selected) return;
    if (action === "reject" && !note.trim()) {
      toast.error("Catatan penolakan wajib diisi");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/orders/${selected.id}/decision`, {
        action,
        owner_note: note,
        adjusted_items: action === "approve"
          ? adjustedItems.map((item) => ({ ...item, qty: Number(item.qty || 0) }))
          : null,
      });
      toast.success(`Order ${action === "approve" ? "disetujui" : "ditolak"}. Notifikasi terkirim ke Mitra.`);
      setSelected(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        title="Pemesanan Masuk"
        subtitle="Review order dari toko binaan · Approve tidak otomatis mengirim — buat Penyaluran manual setelah setuju"
        testId="page-admin-orders"
      />

      <DismissibleMemo
        id="admin-order-workflow"
        className="p-4 pr-12 border-l-4 border-l-blue-500 bg-blue-50/40 mb-4"
        testId="admin-order-info"
      >
        <div className="flex items-start gap-3">
          <ShoppingCart className="w-5 h-5 text-blue-700 shrink-0 mt-0.5"/>
          <div className="text-sm text-slate-700 flex-1">
            <div className="font-bold text-blue-800 mb-1">Anti-tabrakan dengan Penyaluran</div>
            <div className="text-xs">
              <b>Approve</b> = setujui order (janji akan dikirim, tapi belum menyentuh stok gudang). Setelah approve, buka menu <b>Penyaluran Retail</b> untuk membuat pengiriman aktual (di sana stok gudang dikurangi & stok toko bertambah).<br/>
              <b>Reject</b> = tolak order dengan alasan. Tidak ada stok yang bergerak.
            </div>
          </div>
        </div>
      </DismissibleMemo>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending-orders">Menunggu Review ({pendingTF.rows.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history-orders">Riwayat Keputusan ({historyTF.rows.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <div className="p-3 border-b"><TableToolbar tf={pendingTF} placeholder="Cari toko / mitra..."/></div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="w-10">#</TableHead>
                <SortHeader tf={pendingTF} col="created_at">Tanggal Order</SortHeader>
                <SortHeader tf={pendingTF} col="store_name">Toko</SortHeader>
                <SortHeader tf={pendingTF} col="mitra_name">Mitra</SortHeader>
                <TableHead>Item</TableHead>
                <SortHeader tf={pendingTF} col="needed_by">Target Kirim</SortHeader>
                <TableHead className="w-32 text-right">Aksi</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {pendingTF.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Tidak ada order menunggu.</TableCell></TableRow>}
                {pendingTF.rows.map((o, idx) => (
                  <TableRow key={o.id} className="table-row-hover">
                    <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                    <TableCell className="font-mono-num text-xs">{formatDateTime(o.created_at)}</TableCell>
                    <TableCell className="font-medium">{o.store_name}</TableCell>
                    <TableCell className="text-xs">{o.mitra_name || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-md">
                      <OrderItems order={o}/>
                    </TableCell>
                    <TableCell className="text-xs">{o.needed_by ? formatDate(o.needed_by) : "-"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => openReview(o)} className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)] h-7 text-xs" data-testid={`btn-review-${o.id}`}>
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <div className="p-3 border-b"><TableToolbar tf={historyTF} placeholder="Cari status / mitra / catatan..."/></div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="w-10">#</TableHead>
                <SortHeader tf={historyTF} col="decided_at">Tanggal Keputusan</SortHeader>
                <SortHeader tf={historyTF} col="store_name">Toko</SortHeader>
                <SortHeader tf={historyTF} col="mitra_name">Mitra</SortHeader>
                <TableHead>Item</TableHead>
                <SortHeader tf={historyTF} col="status">Status</SortHeader>
                <TableHead>Memo Penutupan</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {historyTF.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Belum ada riwayat.</TableCell></TableRow>}
                {historyTF.rows.map((o, idx) => {
                  const meta = STATUS_META[o.status] || STATUS_META.pending;
                  const Icon = meta.icon;
                  return (
                    <TableRow key={o.id} className="table-row-hover">
                      <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                      <TableCell className="font-mono-num text-xs">{o.decided_at ? formatDateTime(o.decided_at) : "-"}</TableCell>
                      <TableCell className="font-medium">{o.store_name}</TableCell>
                      <TableCell className="text-xs">{o.mitra_name || "-"}</TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-md truncate">
                        <OrderItems order={o}/>
                      </TableCell>
                      <TableCell>
                        <Badge className={meta.color}><Icon className="w-3 h-3 mr-1"/>{meta.label}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-xs truncate">
                        {o.close_memo || o.owner_note || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Review Order */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Review Order Mitra</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="p-3 bg-slate-50 rounded text-sm">
                <div><b>Toko:</b> {selected.store_name}</div>
                <div><b>Mitra:</b> {selected.mitra_name}</div>
                <div><b>Tanggal Order:</b> {formatDateTime(selected.created_at)}</div>
                {selected.needed_by && <div><b>Target Kirim:</b> {formatDate(selected.needed_by)}</div>}
                {selected.notes && <div><b>Catatan Mitra:</b> {selected.notes}</div>}
              </div>
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>Produk</TableHead>
                  <TableHead className="text-right">Diminta</TableHead>
                  <TableHead>Catatan</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(selected.items || []).map((it, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{it.product_name}</TableCell>
                      <TableCell className="text-right font-mono-num font-bold">{it.qty}</TableCell>
                      <TableCell className="text-xs text-slate-500">{it.notes || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {action === "approve" && (
                <div className="border border-emerald-200 rounded-md overflow-hidden">
                  <div className="bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800">
                    Jumlah Pengiriman yang Disetujui
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Produk</TableHead>
                        <TableHead className="w-28 text-right">Diminta</TableHead>
                        <TableHead className="w-36 text-right">Disetujui</TableHead>
                        <TableHead>Catatan Penyesuaian</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adjustedItems.map((item, index) => (
                        <TableRow key={item.product_id}>
                          <TableCell className="font-medium">{item.product_name}</TableCell>
                          <TableCell className="text-right font-mono-num">
                            {selected.items[index]?.qty}
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min={0}
                              value={item.qty}
                              onChange={(event) => updateAdjustedItem(index, "qty", event.target.value)}
                              className="h-8 text-right font-mono-num"
                              data-testid={`input-approved-qty-${index}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.notes}
                              onChange={(event) => updateAdjustedItem(index, "notes", event.target.value)}
                              placeholder="Opsional bila jumlah diubah"
                              data-testid={`input-adjustment-note-${index}`}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={action === "approve" ? "default" : "outline"}
                  onClick={() => setAction("approve")}
                  className={action === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                  data-testid="btn-action-approve"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2"/> Approve (setuju kirim)
                </Button>
                <Button
                  variant={action === "reject" ? "default" : "outline"}
                  onClick={() => setAction("reject")}
                  className={action === "reject" ? "bg-red-600 hover:bg-red-700 text-white" : ""}
                  data-testid="btn-action-reject"
                >
                  <XCircle className="w-4 h-4 mr-2"/> Reject
                </Button>
              </div>

              <div>
                <Label>Catatan untuk Mitra {action === "reject" && <span className="text-red-500">*</span>}</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder={action === "approve" ? "mis. Akan dikirim minggu depan, qty sesuai stok tersedia" : "Alasan penolakan"}
                  data-testid="input-order-decision-note"/>
                <div className="mt-2 flex flex-wrap gap-2">
                  {NOTE_TEMPLATES.map((template, index) => (
                    <Button
                      key={template}
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setNote((current) => current ? `${current} ${template}` : template)}
                      className="h-auto whitespace-normal text-left text-xs"
                      data-testid={`btn-note-template-${index}`}
                    >
                      Template {index + 1}
                    </Button>
                  ))}
                </div>
              </div>

              {action === "approve" && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
                  <b>Setelah Approve:</b> Order akan berstatus "Disetujui" tapi stok gudang belum dikurangi. Buka menu <b>Penyaluran Retail</b> untuk membuat pengiriman aktual (di sana barang benar-benar keluar & masuk ke toko).
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Batal</Button>
            {action === "approve" && selected?.status === "pending" && (
              <Button
                variant="outline"
                onClick={() => nav("/distributions")}
                className="border-emerald-400 text-emerald-700"
                data-testid="btn-go-to-distributions"
              >
                <ArrowRight className="w-4 h-4 mr-2"/> Ke Penyaluran
              </Button>
            )}
            <Button onClick={submit} disabled={busy} className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]" data-testid="btn-submit-order-decision">
              {busy ? "Memproses..." : "Simpan Keputusan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
